import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';


const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise;

const loadTurnstile = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    const shouldAppendScript = !script;
    const handleLoad = () => {
      if (!window.turnstile) {
        reject(new Error('Turnstile did not load.'));
        return;
      }
      resolve(window.turnstile);
    };
    const handleError = () => reject(new Error('Turnstile failed to load.'));

    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (shouldAppendScript) {
      document.head.appendChild(script);
    }
  }).catch((error) => {
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
};

const TurnstileWidget = ({ action, onError, onExpire, onVerify, resetKey = 0 }) => {
  const containerRef = useRef(null);
  const callbacksRef = useRef({ onError, onExpire, onVerify });
  const [loadError, setLoadError] = useState('');
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  callbacksRef.current = { onError, onExpire, onVerify };

  useEffect(() => {
    if (!siteKey) {
      setLoadError('Security check is not configured.');
      return undefined;
    }

    let disposed = false;
    let widgetId;
    setLoadError('');

    loadTurnstile()
      .then((turnstile) => {
        if (disposed || !containerRef.current) {
          return;
        }
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          appearance: 'interaction-only',
          size: 'flexible',
          theme: 'dark',
          callback: (token) => callbacksRef.current.onVerify(token),
          'expired-callback': () => callbacksRef.current.onExpire(),
          'error-callback': () => {
            callbacksRef.current.onError();
            return false;
          },
        });
      })
      .catch(() => {
        if (!disposed) {
          setLoadError('Security check could not load. Please refresh and try again.');
          callbacksRef.current.onError();
        }
      });

    return () => {
      disposed = true;
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [action, resetKey, siteKey]);

  return (
    <Box sx={{ width: '100%', minHeight: loadError ? 'auto' : 1 }}>
      <Box ref={containerRef} sx={{ width: '100%' }} />
      {loadError && (
        <Typography variant="body2" role="alert" sx={{ color: 'error.main' }}>
          {loadError}
        </Typography>
      )}
    </Box>
  );
};

export default TurnstileWidget;
